import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useMinisiteSessionAnalytics } from '../analytics/webAnalytics';
import MonthlyLeadersScreen from './minisite/MonthlyLeadersScreen';
import RemoteNameKeyboard from './minisite/RemoteNameKeyboard';
import { fetchMinisiteLeaderboard, submitMinisiteLeaderboard } from '../api/minisiteLeaderboard';
import { qualifiesForLeaderboard } from '../utils/minisiteLeaderboardQualify';
import './MemoryCardGame.css';
import './minisite/MinisiteShared.css';

const COLS  = 4;
const ROWS  = 4;
const TOTAL = COLS * ROWS; // 16 cards = 8 pairs

// Header + footer + flex padding (must fit 100dvh with grid)
const CHROME_H = 118;
const CHROME_W = 40;
// Gap between cards (matches CSS clamp — use the mid value for JS calc)
const GAP = 10;

function computeCardSize() {
  const maxW = window.innerWidth  - CHROME_W;
  const maxH = window.innerHeight - CHROME_H;
  const byWidth  = Math.floor((maxW - GAP * (COLS - 1)) / COLS);
  const byHeight = Math.floor((maxH - GAP * (ROWS - 1)) / ROWS);
  return Math.max(48, Math.min(byWidth, byHeight, 130));
}

const EMOJI_POOL = [
  '🦁', '🐯', '🐻', '🦊', '🐼', '🐨', '🦄', '🐸',
  '🦋', '🐙', '🦀', '🐬', '🦜', '🦩', '🐢', '🦝',
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildDeck() {
  const symbols = EMOJI_POOL.slice(0, TOTAL / 2);
  return shuffle([...symbols, ...symbols]).map((symbol, idx) => ({
    id: idx,
    symbol,
    flipped: false,
    matched: false,
  }));
}

function MemoryCardGame() {
  useMinisiteSessionAnalytics();
  const [cards, setCards] = useState([]);
  const [cursor, setCursor] = useState(0); // index 0-15
  const [phase, setPhase] = useState('idle'); // 'idle' | 'playing' | 'won'
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const pageRef = useRef(null);

  const [screen, setScreen] = useState('leaders');
  const [winStep, setWinStep] = useState('idle');
  const [joinSel, setJoinSel] = useState('no');
  const [nameInput, setNameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const finalMovesRef = useRef(0);

  const firstFlipped = useRef(null);
  const locked = useRef(false);
  const mismatchIds = useRef([]);

  useEffect(() => {
    const apply = () => {
      if (pageRef.current) {
        pageRef.current.style.setProperty('--card-size', `${computeCardSize()}px`);
      }
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  const startGame = useCallback(() => {
    setCards(buildDeck());
    setCursor(0);
    setMoves(0);
    setMatches(0);
    firstFlipped.current = null;
    locked.current = false;
    mismatchIds.current = [];
    setPhase('playing');
  }, []);

  useLayoutEffect(() => {
    if (screen !== 'game') return undefined;
    startGame();
    const apply = () => {
      if (pageRef.current) {
        pageRef.current.style.setProperty('--card-size', `${computeCardSize()}px`);
      }
    };
    apply();
    const id = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(id);
  }, [screen, startGame]);

  useEffect(() => {
    if (phase !== 'won') return undefined;
    finalMovesRef.current = moves;
    let cancelled = false;
    setWinStep('loading');
    (async () => {
      try {
        const d = await fetchMinisiteLeaderboard('memory');
        if (cancelled) return;
        const entries = d.entries || [];
        const show = qualifiesForLeaderboard('memory', entries, moves);
        setWinStep(show ? 'joinAsk' : 'restartOnly');
      } catch {
        if (!cancelled) setWinStep('restartOnly');
      }
    })();
    return () => { cancelled = true; };
  }, [phase, moves]);

  useEffect(() => {
    if (phase === 'playing') {
      setWinStep('idle');
    }
  }, [phase]);

  const goToLeaders = useCallback(() => {
    setPhase('idle');
    setWinStep('idle');
    setNameInput('');
    setJoinSel('no');
    setCards([]);
    setScreen('leaders');
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (winStep === 'name') {
        return;
      }

      if (phase === 'won' && winStep === 'loading') {
        e.preventDefault();
        return;
      }

      if (phase === 'won' && winStep === 'joinAsk') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          setJoinSel((v) => (v === 'no' ? 'yes' : 'no'));
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          if (joinSel === 'yes') {
            setNameInput('');
            setWinStep('name');
            return;
          }
          goToLeaders();
        }
        return;
      }

      if (phase === 'won' && winStep === 'thanks') {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          goToLeaders();
        }
        return;
      }

      if (e.key === 'Enter' || e.keyCode === 13) {
        if (phase === 'idle' || phase === 'won') {
          if (phase === 'won' && winStep === 'restartOnly') {
            startGame();
            e.preventDefault();
            return;
          }
          if (phase === 'idle') {
            startGame();
            e.preventDefault();
            return;
          }
        }
      }

      if (phase !== 'playing') return;

      if (e.key === 'ArrowRight') {
        setCursor(c => (c % COLS === 0 ? c : c - 1));
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowLeft') {
        setCursor(c => (c % COLS === COLS - 1 ? c : c + 1));
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowDown') {
        setCursor(c => (c + COLS < TOTAL ? c + COLS : c));
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowUp') {
        setCursor(c => (c - COLS >= 0 ? c - COLS : c));
        e.preventDefault();
        return;
      }

      if (e.key === 'Enter' || e.keyCode === 13) {
        e.preventDefault();
        if (locked.current) return;

        setCards(prev => {
          const card = prev[cursor];
          if (card.flipped || card.matched) return prev;

          const updated = prev.map((c, i) =>
            i === cursor ? { ...c, flipped: true } : c
          );

          if (firstFlipped.current === null) {
            firstFlipped.current = cursor;
            return updated;
          }

          const firstIdx = firstFlipped.current;
          const firstCard = updated[firstIdx];
          const secondCard = updated[cursor];
          firstFlipped.current = null;

          setMoves(m => m + 1);

          if (firstCard.symbol === secondCard.symbol) {
            const matched = updated.map(c =>
              c.id === firstCard.id || c.id === secondCard.id
                ? { ...c, matched: true }
                : c
            );
            setMatches((prevM) => {
              const newMatches = prevM + 1;
              if (newMatches === TOTAL / 2) {
                setTimeout(() => setPhase('won'), 600);
              }
              return newMatches;
            });
            return matched;
          }
          locked.current = true;
          mismatchIds.current = [firstCard.id, secondCard.id];

          setTimeout(() => {
            setCards(cur => cur.map(c => {
              if (c.id === firstCard.id || c.id === secondCard.id) {
                return { ...c, flipped: false };
              }
              return c;
            }));
            mismatchIds.current = [];
            locked.current = false;
          }, 900);

          return updated;
        });
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, cursor, startGame, winStep, joinSel, goToLeaders]);

  const handleNameDone = useCallback(async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    const scoreVal = finalMovesRef.current;
    try {
      await submitMinisiteLeaderboard('memory', trimmed, scoreVal);
      setWinStep('thanks');
    } catch {
      setWinStep('thanks');
    } finally {
      setSubmitting(false);
    }
  }, [nameInput, submitting]);

  const handleNameCancel = useCallback(() => {
    setWinStep('joinAsk');
    setNameInput('');
  }, []);

  const onLeadersContinue = useCallback(() => {
    setScreen('game');
  }, []);

  if (screen === 'leaders') {
    return (
      <MonthlyLeadersScreen
        game="memory"
        scoreLabel="מהלכים"
        onContinue={onLeadersContinue}
      />
    );
  }

  return (
    <div className="memory-page" ref={pageRef} dir="rtl">
      <div className="memory-header">
        <div className="memory-title">כרטיסי זיכרון</div>
        <div className="memory-stats">
          <div className="memory-stat">
            <span className="memory-stat-label">זוגות</span>
            <span className="memory-stat-value">{matches}/{TOTAL / 2}</span>
          </div>
          <div className="memory-stat">
            <span className="memory-stat-label">מהלכים</span>
            <span className="memory-stat-value">{moves}</span>
          </div>
        </div>
      </div>

      <div className="memory-play">
        <div className="memory-grid">
        {cards.map((card, idx) => {
          const isCursor = idx === cursor && phase === 'playing';
          const isMismatch = mismatchIds.current.includes(card.id);
          let cls = 'memory-card';
          if (card.flipped) cls += ' flipped';
          if (card.matched) cls += ' matched';
          if (isCursor) cls += ' cursor';
          if (isMismatch) cls += ' mismatch';

          return (
            <div key={card.id} className={cls}>
              <div className="memory-card-inner">
                <div className="memory-card-back">⬡</div>
                <div className="memory-card-front">{card.symbol}</div>
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {phase === 'won' && (
        <div className="memory-overlay">
          <div className="memory-overlay-title">כל הכבוד! ניצחת 🎉</div>
          <div className="memory-overlay-stat">הושלם ב־{moves} מהלכים</div>
          {winStep === 'loading' && (
            <div className="minisite-overlay-busy">בודקים זכאות לטבלה…</div>
          )}
          {winStep === 'restartOnly' && (
            <div className="memory-overlay-hint">לחץ Enter לסיבוב נוסף</div>
          )}
          {winStep === 'joinAsk' && (
            <div className="minisite-join">
              <p className="minisite-join-q">להצטרף למצטייני החודש?</p>
              <div className="minisite-join-options">
                <span className={`minisite-join-opt ${joinSel === 'no' ? 'minisite-join-opt--on' : ''}`}>לא</span>
                <span className={`minisite-join-opt ${joinSel === 'yes' ? 'minisite-join-opt--on' : ''}`}>כן</span>
              </div>
              <div className="memory-overlay-hint">← → לבחירה · Enter לאישור</div>
            </div>
          )}
          {winStep === 'name' && (
            <RemoteNameKeyboard
              value={nameInput}
              onChange={setNameInput}
              onDone={handleNameDone}
              onCancel={handleNameCancel}
            />
          )}
          {winStep === 'thanks' && (
            <div className="memory-overlay-hint">נרשמת בהצלחה! לחץ Enter לחזרה למצטייני החודש</div>
          )}
          {submitting && <div className="minisite-overlay-busy">שומר…</div>}
        </div>
      )}

      <div className="memory-footer">↑ ↓ ← → לניווט &nbsp;·&nbsp; Enter להפוך כרטיס</div>
    </div>
  );
}

export default MemoryCardGame;
